import * as R from 'ramda'
import {update} from './ObjectHelper'
import * as PageSection from './PageSection'
import {Paginated} from './Paginated'

export interface Pagination<T> {
    readonly data: T[]
    readonly totalCount: number
    readonly pageSection: PageSection.M
}

export type Navigation =
    | First
    | Previous
    | Next
    | Last

interface First {
    type: 'First'
}

interface Previous {
    type: 'Previous'
}

interface Next {
    type: 'Next'
}

interface Last {
    type: 'Last'
}

export function numberOfPages<T>(
    {totalCount, pageSection}: Pagination<T>
): number {
    return Math.max(1, Math.ceil(totalCount / pageSection.itemsPerPage))
}

export function navigate<T>(
    navigation: Navigation,
    pagination: Pagination<T>,
): Pagination<T> {
    const
        {pageSection} = pagination,
        {currentPage} = pageSection,
        nextPage =
            (() => {
                switch (navigation.type) {
                    case 'First':
                        return 1
                    case 'Previous':
                        return R.clamp(
                            1, numberOfPages(pagination), currentPage - 1
                        )
                    case 'Next':
                        return R.clamp(
                            1, numberOfPages(pagination), currentPage + 1
                        )
                    case 'Last':
                        return numberOfPages(pagination)
                }
            })()
    return update(
        {pageSection: update({currentPage: nextPage}, pagination.pageSection)},
        pagination,
    )
}

export function setItemsPerPage<T>(
    itemsPerPage: number,
    pagination: Pagination<T>
): Pagination<T> {
    return update(
        {
            pageSection:
                update({itemsPerPage, currentPage: 1}, pagination.pageSection),
        },
        pagination,
    )
}

export function setData<T>(
    paginated: Paginated<T>,
    pagination: Pagination<T>
): Pagination<T> {
    return update(paginated, pagination)
}

export function rangeDisplay<T>(
    {totalCount, pageSection}: Pagination<T>,
): string {
    const
        {currentPage, itemsPerPage} = pageSection,
        start = (currentPage - 1) * itemsPerPage + 1,
        end = Math.min(totalCount, currentPage * itemsPerPage)
    return `${start} - ${end}`
}

export function fromPaginated<T>(
    pageSection: PageSection.M,
    {data, totalCount}: Paginated<T>
): Pagination<T> {
    return {
        data,
        totalCount,
        pageSection,
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Data/Pagination.ts